import React from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/subpage.css";
import "../../Styles/pages/heads-of-ta.css";
import useReveal from "./useReveal";

export default function HeadsOfTA() {
  const ref = useReveal();

  return (
    <div className="page ann-subpage" ref={ref}>
      <section className="sub-hero" id="top">
        <span className="eye">For Heads of Talent Acquisition</span>
        <h1>Quality at <em>volume</em>. And a shortlist they actually trust<span className="period" /></h1>
        <p className="lede-xl">
          Your team is drowning in résumés, and your hiring managers still don't trust the shortlist. <b>Ann reads every applicant with the care your sharpest sourcer gives one</b> — so the right people surface at your real volume, and the shortlist arrives with the evidence that ends the second-guessing.
        </p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/how-it-works" className="btn btn-secondary btn-lg">How Ann works</Link>
        </div>
      </section>

      {/* THE SQUEEZE */}
      <section className="sub-section">
        <div className="cols-split">
          <div className="sub-head" style={{ marginBottom: 0 }}>
            <span className="eye">The squeeze</span>
            <h2 className="sub-h2">More requisitions. Same team. <em>Higher</em> bar.</h2>
          </div>
          <div className="prose">
            <p className="big">Every year the req load goes up, the headcount to handle it doesn't, and the hiring managers get pickier about what "qualified" means.</p>
            <p>So your recruiters triage — reading the top, skimming the middle, never reaching the bottom where good people sit mis-described. The shortlist reflects who had time to read, not who's best.</p>
            <p>Ann removes the triage. Every applicant gets the same careful read, so the shortlist reflects the pool — not your team's capacity that week.</p>
          </div>
        </div>
      </section>

      {/* THE FORWARDING MOMENT */}
      <section className="sub-section bone">
        <div className="cols-split">
          <div>
            <span className="eye" style={{ display: "block", marginBottom: 20 }}>The moment that decides everything</span>
            <h2 className="sub-h2">Win the <em>forwarding moment</em>.</h2>
            <p className="prose" style={{ marginTop: 8 }}>When a recruiter forwards a candidate, the hiring manager spends about <b>thirty seconds</b> deciding whether anyone really looked. If the shortlist can't survive that glance, the loop jams — and your team gets blamed for slow hiring it doesn't control. Ann makes every candidate arrive with the case already made.</p>
          </div>
          <div className="fwd">
            <div className="row"><div className="av" style={{ background: "var(--src-talent)" }}>PS</div><div className="nm">Priya S.<span>Sr CS Manager · 6y</span></div><span className="ok">case attached</span></div>
            <div className="row"><div className="av" style={{ background: "var(--src-quick)" }}>VK</div><div className="nm">Vikram K.<span>CS Lead · 5y</span></div><span className="ok">case attached</span></div>
            <div className="row"><div className="av" style={{ background: "var(--src-jobpost)" }}>AR</div><div className="nm">Arjun R.<span>Ops Manager · 7y</span></div><span className="ok">case attached</span></div>
            <div className="cap">// Every name carries its evidence. The manager reads <em>why</em> before they reply.</div>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="sub-section">
        <div className="sub-head">
          <span className="eye">What you get</span>
          <h2 className="sub-h2">Capacity, consistency, and a <em>defensible</em> shortlist.</h2>
        </div>
        <div className="cols-3">
          <div className="point">
            <div className="num">01 · Capacity</div>
            <h3>Read the <em>whole</em> pool.</h3>
            <p>Thousands of applicants, the same careful read each — including the ones your team never had time to reach. No more triage by time-of-day.</p>
          </div>
          <div className="point">
            <div className="num">02 · Consistency</div>
            <h3>One bar, every <em>req</em>.</h3>
            <p>The standard doesn't swing with whoever ran the search. Ann holds the same evidence-based bar across recruiters, roles, and weeks.</p>
          </div>
          <div className="point">
            <div className="num">03 · Trust</div>
            <h3>Evidence they <em>believe</em>.</h3>
            <p>Each shortlisted candidate comes with cited evidence and a confidence read. The hiring manager stops re-screening and starts interviewing.</p>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="sub-section bone">
        <div className="sub-head"><span className="eye">What changes</span><h2 className="sub-h2">The numbers your VP asks about.</h2></div>
        <div className="metrics">
          <div className="m"><div className="v">+47%</div><div className="k">candidates surfaced<br />vs. keyword search</div></div>
          <div className="m"><div className="v">+34%</div><div className="k">shortlist-to-interview<br />conversion</div></div>
          <div className="m"><div className="v">~18h</div><div className="k">per recruiter / week<br />back from triage</div></div>
        </div>
        <p className="prose" style={{ marginTop: 28, fontSize: 13, color: "var(--ink-4)", fontFamily: "var(--f-mono)", letterSpacing: ".04em" }}>// Figures illustrative of early design-partner pilots.</p>
      </section>

      {/* PROOF */}
      <section className="sub-section dark">
        <span className="eye" style={{ display: "block", marginBottom: 32 }}>From a Head of Talent</span>
        <p className="pquote">"My hiring managers stopped <span className="cy">re-screening</span> our shortlists. That alone gave the team back a day a week."</p>
        <p className="pattrib"><span className="nm">Head of Talent</span> · Series B fintech · 1,200 hires / year</p>
      </section>

      {/* CTA */}
      <section className="sub-cta">
        <span className="eye">On a live requisition</span>
        <h2>Run a <em>real</em> req through Ann.</h2>
        <p className="sub">Fifteen minutes on an open role. See the shortlist — and the evidence behind each name — your managers would actually trust.</p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/why-ann" className="btn btn-secondary btn-lg">Why Ann</Link>
        </div>
      </section>
    </div>
  );
}
